import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService } from '../services/authService';
import { testAuth } from '../utils/testAuth';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [backendAvailable, setBackendAvailable] = useState(true);

  useEffect(() => {
    checkAuthStatus();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const checkAuthStatus = async () => {
    try {
      // First check if backend is available
      const isBackendUp = await testAuth.checkBackendHealth();
      setBackendAvailable(isBackendUp);

      const token = localStorage.getItem('access_token');
      if (token) {
        if (isBackendUp) {
          // Use real backend
          const userData = await authService.getProfile();
          setUser(userData);
          setIsAuthenticated(true);
        } else {
          // Use mock data for development
          const mockUser = JSON.parse(localStorage.getItem('mock_user') || '{}');
          if (mockUser.email) {
            setUser(mockUser);
            setIsAuthenticated(true);
          }
        }
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      if (backendAvailable) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('mock_user');
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      if (backendAvailable) {
        // Use real backend
        const response = await authService.login(email, password);
        localStorage.setItem('access_token', response.access);
        localStorage.setItem('refresh_token', response.refresh);
        
        const userData = await authService.getProfile();
        setUser(userData);
        setIsAuthenticated(true);
        
        return { success: true };
      } else {
        // Use mock authentication for development
        if (email === testAuth.testUser.email && password === testAuth.testUser.password) {
          const mockResult = testAuth.mockLogin();
          setUser(mockResult.user);
          setIsAuthenticated(true);
          return { success: true };
        } else {
          return { 
            success: false, 
            error: 'Invalid credentials (use test@aicarousel.com / testpass123 for development)' 
          };
        }
      }
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await authService.register(userData);
      localStorage.setItem('access_token', response.access);
      localStorage.setItem('refresh_token', response.refresh);
      
      const userProfile = await authService.getProfile();
      setUser(userProfile);
      setIsAuthenticated(true);
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data || 'Registration failed' 
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('mock_user');
    setUser(null);
    setIsAuthenticated(false);
  };

  const isPaidUser = user?.is_paid_user === true;

  const refreshUser = async () => {
    try {
      const userData = await authService.getProfile();
      setUser(userData);
    } catch (error) {
      console.error('Failed to refresh user profile:', error);
    }
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    backendAvailable,
    isPaidUser,
    login,
    register,
    logout,
    checkAuthStatus,
    refreshUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
